import { FC, ReactNode } from 'react'

import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { routes } from 'commons/constants/routes'
import { Request } from 'commons/utils/request'
import { Toast, handleFormSubmissionError } from 'ui/components'
import { annualReviewSalesDepositsEditModeQueryOptions } from '~bank-annual-review/service'
import { AnnualReviewSalesDepositsForm } from '~bank-annual-review/types/SalesDeposits'

import { AnnualReviewSalesDepositsContext } from './AnnualReviewSalesDepositsContext'

type AnnualReviewSalesDepositsContextProviderProps = {
  children: ReactNode
}

export const AnnualReviewSalesDepositsContextProvider: FC<AnnualReviewSalesDepositsContextProviderProps> = ({
  children
}) => {
  const routeApi = getRouteApi('/_app/annual-review/$annualReviewId/edit-sales-and-deposit/$id')

  const navigate = routeApi.useNavigate()
  const id = routeApi.useParams({ select: (params) => Number(params.id) })
  const annualReviewId = routeApi.useParams({ select: (params) => Number(params.annualReviewId) })

  const annualReviewSalesDepositsEditModeQuery = useQuery(annualReviewSalesDepositsEditModeQueryOptions(id))
  const companyId = annualReviewSalesDepositsEditModeQuery.data?.companyId ?? 0

  const backToAnnualReviewDetails = () => {
    navigate({
      to: routes.bank.annualReview.details,
      params: { companyId: companyId.toString(), id: annualReviewId.toString() }
    })
  }

  const { mutate: saveSalesDeposits } = useMutation<void, Error, AnnualReviewSalesDepositsForm>({
    mutationFn: (opts) => Request.put(Request.urls.bank.annualReviewSalesDeposits, { form: opts }),
    onSuccess: async () => {
      Toast.successSmall('Annual Review Sales & Deposit was successfully updated.')
      backToAnnualReviewDetails()
    },
    onError: (error) => {
      handleFormSubmissionError<AnnualReviewSalesDepositsForm>(error, formApi)
    }
  })

  const formApi = useForm({
    defaultValues: annualReviewSalesDepositsEditModeQuery.data?.form,
    onSubmit: async ({ value }) => await saveSalesDeposits(value)
  })

  if (!annualReviewSalesDepositsEditModeQuery.data) return null

  const value = {
    id,
    annualReviewId,
    companyId,
    data: annualReviewSalesDepositsEditModeQuery.data,
    formApi,
    isPending:
      annualReviewSalesDepositsEditModeQuery.isLoading ||
      annualReviewSalesDepositsEditModeQuery.isRefetching ||
      annualReviewSalesDepositsEditModeQuery.isPending,
    successFullyLoaded: !!annualReviewSalesDepositsEditModeQuery.data,
    backToAnnualReviewDetails
  }

  return <AnnualReviewSalesDepositsContext.Provider value={value}>{children}</AnnualReviewSalesDepositsContext.Provider>
}
